// frontend/src/components/QRScanner.jsx
// Uses html5-qrcode to open device camera and scan QR codes.
// When a QR is decoded, it extracts the clientId and calls the scan API.

import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { api } from '../utils/api';
import { useLanguage } from '../utils/i18n';
import { useNotification } from '../utils/NotificationContext';

export default function QRScanner({ onClose }) {
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const scannerRef = useRef(null);
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scannedClientId, setScannedClientId] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    const scannerId = 'qr-scanner-container';
    const scanner = new Html5Qrcode(scannerId);
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: 'environment' }, // use back camera on mobile
      { fps: 10, qrbox: { width: 250, height: 250 } },
      async (decodedText) => {
        // Stop scanning once we have a result
        await scanner.stop().catch(() => { });
        setScanning(false);

        // Parse clientId from the URL encoded in QR
        // URL format: http://localhost:4000/scan-qr?clientId=xxxxx
        try {
          const url = new URL(decodedText);
          const clientId = url.searchParams.get('clientId');
          if (!clientId) throw new Error(t('err_invalid_qr'));

          setScannedClientId(clientId);
          const data = await api.scanQR(clientId);
          setResult(data);
        } catch (err) {
          showNotification(err.message, 'error');
        }
      },
      (err) => { /* ignore per-frame errors */ }
    ).then(() => setScanning(true))
      .catch(err => showNotification(`${t('err_camera_denied')}: ${err}`, 'error'));

    return () => {
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => { });
      }
    };
  }, []);

  const handleRedeem = async () => {
    if (!scannedClientId) return;
    setRedeeming(true);
    try {
      const data = await api.redeemReward(scannedClientId);
      showNotification(t('notify_reward_redeemed'), 'success');
      // Update result locally to reflect reset
      setResult(prev => ({
        ...prev,
        newCount: 0,
        rewardEarned: false,
        message: data.message
      }));
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{t('scan_title')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {!result && (
          <>
            <div id="qr-scanner-container" style={{ width: '100%', borderRadius: 8, overflow: 'hidden' }} />
            {scanning && <p style={{ textAlign: 'center', marginTop: 12, color: 'var(--text-muted)', fontSize: 14 }}>{t('point_camera')}</p>}
          </>
        )}

        {result && (
          <div>
            <div className="alert alert-success" style={{ fontSize: 16, textAlign: 'center' }}>
              {result.message}
            </div>
            <div className="card" style={{ background: 'var(--bg)', marginBottom: 16 }}>
              <p><strong>{t('client_label')}:</strong> {result.clientName}</p>
              <p style={{ marginTop: 8 }}><strong>{t('visits_label')}:</strong> {result.newCount} / {result.threshold}</p>
              <div className="progress-bar-track" style={{ marginTop: 10 }}>
                <div className="progress-bar-fill" style={{ width: `${Math.min(100, (result.newCount / result.threshold) * 100)}%` }} />
              </div>
            </div>
            {result.rewardEarned && (
              <>
                <div className="alert" style={{ background: '#FFF9C4', color: '#827717', textAlign: 'center', fontSize: 18, marginBottom: 12 }}>
                  {t('reward_earned')}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleRedeem}
                  disabled={redeeming}
                  style={{ width: '100%', justifyContent: 'center', marginBottom: 8, fontSize: 16, padding: '12px' }}
                >
                  {redeeming ? t('please_wait') : t('gift')}
                </button>
              </>
            )}
            <button
              className={`btn ${result.rewardEarned ? 'btn-outline' : 'btn-primary'}`}
              onClick={onClose}
              style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
            >
              {t('done')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
